import { forwardRef } from 'react';
import { useTextField } from 'react-aria';

import { useObjectRef } from '@react-aria/utils';
import { cn } from '@/lib/utils';

export interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  label?: string;
  description?: string;
  value?: Array<string>;
  onUpdate: (value: Array<string>) => void;
}

const MultiValueTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, error, label, description, ...props }, forwardedRef) => {
    const ref = useObjectRef(forwardedRef);

    const { labelProps, inputProps, descriptionProps, errorMessageProps } =
      useTextField(
        {
          label,
          description,
          errorMessage: error,
          inputElementType: 'textarea',
          value: props.value?.join('\n'),
          onChange: (v) => props?.onUpdate(v.split('\n')),
          placeholder: props.placeholder || 'Enter value(s)',
        },
        ref
      );

    return (
      <div className='w-full'>
        {label && (
          <label
            {...labelProps}
            className='block text-sm font-medium text-gray-700 mb-1'
          >
            {label}
          </label>
        )}
        <textarea
          {...props}
          {...inputProps}
          ref={ref}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-red-500 focus-visible:ring-red-500',
            className
          )}
        />
        {description && (
          <p {...descriptionProps} className='mt-1 text-sm text-gray-500'>
            {description}
          </p>
        )}
        {error && (
          <p {...errorMessageProps} className='mt-1 text-sm text-red-500'>
            {error}
          </p>
        )}
      </div>
    );
  }
);

MultiValueTextArea.displayName = 'MultiValueTextArea';

export { MultiValueTextArea };
